/**
 * RedirectToScopedIfNeeded — wraps the FLAT (legacy) customer-scoped
 * routes so that when a super_admin or reseller_owner has a customer
 * context set in Zustand, the URL is auto-rewritten to the scoped form
 * (`/customer/:cn/...` or `/reseller/:rn/customer/:cn/...`).
 *
 * This lets every existing `<Link to="/recordings">` keep working —
 * clicking it bumps the URL bar to the right scoped form without each
 * call-site needing to know about scopes. Internal navigation that
 * already produces scoped URLs (via useScopedNavigate / scopedHref)
 * bypasses this guard entirely.
 *
 * Roles without a Zustand customer context (office_manager / standard_user
 * / limited_user / agent) keep using flat URLs — their tenant scope is
 * implicit from their JWT and there's nothing to encode in the path.
 * That can be addressed in a follow-up pass if Sam wants them to see the
 * customer number in their URL bar too (backlog item).
 *
 * Bookmarks of OLD flat URLs that pre-date scoping continue to resolve:
 * the user lands on the flat route, the guard sees their context, and
 * the URL is replaced (replace=true) in history so back-button works as
 * if they'd typed the scoped URL.
 */
import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth-store';
import { useCustomerContext } from '@/lib/customer-context-store';
import { useResellerContext } from '@/lib/reseller-context-store';
import { buildScopedPath, isCustomerScopedPath } from '@/lib/scopedUrl';

export function RedirectToScopedIfNeeded({ children }: { children: ReactNode }) {
  const user = useAuth((s) => s.user);
  const customer = useCustomerContext((s) => s.current);
  const reseller = useResellerContext((s) => s.current);
  const { pathname, search, hash } = useLocation();

  // Only super_admin and reseller_owner navigate across customers and
  // therefore benefit from having the customer number in the URL.
  const isSuper = Boolean(user?.is_super_admin) || user?.role === 'super_admin';
  const isReseller = user?.role === 'reseller_owner';
  if (!isSuper && !isReseller) return <>{children}</>;

  if (customer?.customerNumber == null) return <>{children}</>;
  if (!isCustomerScopedPath(pathname)) return <>{children}</>;

  // Super_admin without a reseller context is still a valid state (they
  // haven't drilled in via /resellers yet) — but the URL we'd build would
  // omit the reseller segment. Skip the rewrite in that case; the user
  // will navigate through /resellers → /customers and pick up the full
  // scope organically.
  if (isSuper && reseller?.resellerNumber == null) return <>{children}</>;

  const scoped = buildScopedPath(pathname, {
    customerNumber: customer.customerNumber,
    resellerNumber: isSuper ? reseller?.resellerNumber ?? null : null,
  });
  if (scoped === pathname) return <>{children}</>;
  return <Navigate to={`${scoped}${search}${hash}`} replace />;
}
